import { memo, useMemo } from 'react';

import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import Money from '#client/components/ListTransaction/components/Money';
import DirectionTypeIcon from '#client/components/TransactionDirectionTypeIcon';
import { DATE_FORMAT } from '#client/constants/date';
import {
  INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS,
  MONEY_COLOR,
  TRANSACTION_DIRECTION
} from '#client/constants/transaction';
import useModalContext from '#client/hooks/useModalContext';
import { formatAccountNumber } from '#client/utils/text';

import DebitCardPaymentDetailModal from './DebitCardPaymentDetailModal';
import { StackStyled } from './DebitCardPaymentRow.styles';

function DebitCardPaymentRow({
  routineStatus,
  amount,
  createdDatetime,
  updatedDatetime,
  cardInfo,
  uuid
}) {
  const { openModal } = useModalContext();

  const clickHandler = () => {
    openModal({
      component: DebitCardPaymentDetailModal,
      uuid,
      routineStatus,
      amount,
      createdDatetime,
      updatedDatetime,
      cardInfo
    });
  };

  const moneyColor = useMemo(() => {
    if (routineStatus === INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS.COMPLETED) {
      return MONEY_COLOR.GREEN;
    }

    return MONEY_COLOR.PRIMARY;
  }, [routineStatus]);

  const formatCardNumber = formatAccountNumber({
    accountNumber: cardInfo?.cardLastFourDigits ?? '',
    formatCharacter: '•',
    numberFormatCharacter: 2
  });

  const summary = `Deposit from card ${formatCardNumber} via Astra`;
  const dateText = useMemo(() => {
    switch (routineStatus) {
      case INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS.COMPLETED:
        return dayjs(updatedDatetime).format(DATE_FORMAT.PRIMARY);
      case INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS.FAILED:
        return 'Failed';
      default:
        return dayjs(createdDatetime).format(DATE_FORMAT.PRIMARY);
    }
  }, [createdDatetime, routineStatus, updatedDatetime]);

  return (
    <Stack
      onClick={clickHandler}
      role="listitem"
      sx={{
        cursor: 'pointer',
        padding: '15px 10px',
        '&:hover': {
          boxShadow: '0px 4px 16px 2px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <StackStyled
        gap="24px"
        pr={0}
        width="100%"
      >
        <StackStyled width="85%">
          <Stack
            alignItems="center"
            direction="row"
            spacing={{ xs: '16px', md: '32px' }}
          >
            <Stack>
              <DirectionTypeIcon
                direction={TRANSACTION_DIRECTION.DEBIT}
                isPayment
              />
            </Stack>
            <Stack
              alignItems="start"
              direction="column"
              spacing="4opx"
            >
              <Text
                size="md"
                weight="medium"
              >
                {summary}
              </Text>
              <Text
                color="tertiary"
                size="xs"
                whiteSpace="nowrap"
              >
                {dateText}
              </Text>
            </Stack>
          </Stack>
        </StackStyled>
        <Stack
          alignItems="end"
          direction="column"
        >
          {routineStatus !== INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS.FAILED && (
            <Money
              amount={amount}
              color={moneyColor}
              direction={TRANSACTION_DIRECTION.DEBIT}
              isPayment
            />
          )}
          {routineStatus === INSTANT_FUNDING_TRANSFER_ROUTINE_STATUS.FAILED && (
            <Text
              color="tertiary"
              size="xl"
            >
              -
            </Text>
          )}
        </Stack>
      </StackStyled>
    </Stack>
  );
}

DebitCardPaymentRow.propTypes = {
  uuid: PropTypes.string.isRequired,
  routineStatus: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  createdDatetime: PropTypes.string,
  updatedDatetime: PropTypes.string,
  cardInfo: PropTypes.shape({
    cardCompany: PropTypes.string,
    cardLastFourDigits: PropTypes.string,
    cardHolderFirstName: PropTypes.string,
    cardHolderLastName: PropTypes.string,
    cardBillingAddress: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      state: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string
    })
  })
};

export default memo(DebitCardPaymentRow);
